// admin/src/pages/Branding.jsx - ENHANCED WITH REAL QUIZ COMPONENTS & ADA CHECKER
import React, { useState, useEffect, useCallback } from 'react';
import { useAdminAuth } from '../auth/AdminAuthContext';
import TopNav from '../components/TopNav';

// Inline adminFetch
const API_ROOT = process.env.REACT_APP_API_URL || 'http://localhost:5000';

async function adminFetch(path, { secret, method = 'GET', body, headers = {}, ...rest } = {}) {
  const res = await fetch(`${API_ROOT}${path}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      'x-admin-secret': secret || '',
      ...headers,
    },
    body: body ? JSON.stringify(body) : undefined,
    ...rest,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(text || `Request failed: ${res.status}`);
  }
  return res.json();
}

// ============================================================
// ADA CONTRAST CHECKER UTILITY
// ============================================================
function hexToRgb(hex) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : null;
}

function getLuminance(r, g, b) {
  const [rs, gs, bs] = [r, g, b].map(c => {
    c = c / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
}

function getContrastRatio(hex1, hex2) {
  const rgb1 = hexToRgb(hex1);
  const rgb2 = hexToRgb(hex2);
  if (!rgb1 || !rgb2) return 1;
  
  const lum1 = getLuminance(rgb1.r, rgb1.g, rgb1.b);
  const lum2 = getLuminance(rgb2.r, rgb2.g, rgb2.b);
  const lighter = Math.max(lum1, lum2);
  const darker = Math.min(lum1, lum2);
  
  return (lighter + 0.05) / (darker + 0.05);
}

function ContrastChecker({ backgroundColor, textColor, label, size = 'normal' }) {
  const ratio = getContrastRatio(backgroundColor, textColor);
  const normalPass = ratio >= 4.5;
  const largePass = ratio >= 3;
  const aaPass = size === 'large' ? largePass : normalPass;
  const aaaPass = size === 'large' ? ratio >= 4.5 : ratio >= 7;
  
  return (
    <div style={{
      padding: '12px',
      background: '#f8fafc',
      borderRadius: '8px',
      border: '1px solid #e2e8f0',
      marginBottom: '8px'
    }}>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '8px'
      }}>
        <span style={{ fontSize: '13px', fontWeight: 600, color: '#0b1b2a' }}>
          {label}
        </span>
        <span style={{ 
          fontSize: '12px', 
          fontWeight: 700,
          color: aaPass ? '#059669' : '#dc2626'
        }}>
          {ratio.toFixed(2)}:1
        </span>
      </div>
      
      {/* Preview */}
      <div style={{
        padding: '8px 12px',
        background: backgroundColor,
        color: textColor,
        borderRadius: '6px',
        fontSize: size === 'large' ? '18px' : '14px',
        fontWeight: size === 'large' ? 600 : 400,
        marginBottom: '8px'
      }}>
        Sample Text Preview
      </div>
      
      {/* Compliance badges */}
      <div style={{ display: 'flex', gap: '4px' }}>
        <Badge pass={aaPass} label="AA" />
        <Badge pass={aaaPass} label="AAA" />
      </div>
    </div>
  );
}

function Badge({ pass, label }) {
  return (
    <span style={{
      padding: '2px 8px',
      borderRadius: '4px',
      fontSize: '11px',
      fontWeight: 700,
      background: pass ? '#ecfdf5' : '#fef2f2',
      color: pass ? '#059669' : '#dc2626',
      border: `1px solid ${pass ? '#10b981' : '#ef4444'}`
    }}>
      {pass ? '✓' : '✗'} {label}
    </span>
  );
}

// ============================================================
// COMPONENT PREVIEW WRAPPER
// ============================================================
function ComponentPreview({ title, children }) {
  return (
    <div style={{ marginBottom: '24px' }}>
      <div style={{
        fontSize: '11px',
        fontWeight: 700,
        color: '#64748b',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        marginBottom: '8px',
        display: 'flex',
        alignItems: 'center',
        gap: '6px'
      }}>
        <div style={{
          width: '4px',
          height: '4px',
          borderRadius: '50%',
          background: '#0b5fff'
        }} />
        {title}
      </div>
      {children}
    </div>
  );
}

// Default theme structure (UI format - will be mapped to backend format on save)
const DEFAULT_THEME = {
  primaryColor: '#0b5fff',
  primaryColorHover: '#0847cc',
  primaryColorLight: '#e6f0ff',
  secondaryColor: '#8b5cf6',
  secondaryColorHover: '#7c3aed',
  accentColor: '#10b981',
  accentColorHover: '#059669',
  backgroundColor: '#ffffff',
  backgroundLight: '#f8fafc',
  backgroundLighter: '#f1f5f9',
  textPrimary: '#0b1b2a',
  textSecondary: '#64748b',
  textLight: '#94a3b8',
  buttonTextColor: '#ffffff',
  buttonRadius: '8px',
  cardRadius: '12px',
  cardShadow: 'medium',
  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  headingFontFamily: 'inherit'
};

export default function Branding() {
  const { secret } = useAdminAuth();
  const [tenantId] = useState('demo-university');
  const [quizTheme, setQuizTheme] = useState(null);
  const [branding, setBranding] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  const loadTheme = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminFetch(`/api/tenant/${tenantId}/theme?app=quiz`, { secret });
      
      const themeFromDB = res.theme || {};
      
      const mappedTheme = {
        primaryColor: themeFromDB.brandColor || DEFAULT_THEME.primaryColor,
        primaryColorHover: DEFAULT_THEME.primaryColorHover,
        primaryColorLight: DEFAULT_THEME.primaryColorLight,
        buttonTextColor: themeFromDB.buttonTextColor || DEFAULT_THEME.buttonTextColor,
        backgroundColor: themeFromDB.backgroundColor || DEFAULT_THEME.backgroundColor,
        textSecondary: themeFromDB.textSecondary || DEFAULT_THEME.textSecondary,
        fontFamily: themeFromDB.fontFamily || DEFAULT_THEME.fontFamily,
        headingFontFamily: themeFromDB.headingFontFamily || DEFAULT_THEME.headingFontFamily,
        backgroundLight: themeFromDB.backgroundAccent || DEFAULT_THEME.backgroundLight,
        backgroundLighter: DEFAULT_THEME.backgroundLighter,
        textPrimary: themeFromDB.textColor || DEFAULT_THEME.textPrimary,
        textLight: DEFAULT_THEME.textLight,
        secondaryColor: DEFAULT_THEME.secondaryColor,
        secondaryColorHover: DEFAULT_THEME.secondaryColorHover,
        accentColor: DEFAULT_THEME.accentColor,
        accentColorHover: DEFAULT_THEME.accentColorHover,
        buttonRadius: themeFromDB.buttonStyle === 'pill' ? '999px' :
                     themeFromDB.buttonStyle === 'square' ? '4px' : '8px',
        cardRadius: themeFromDB.cardStyle === 'square' ? '4px' : '12px',
        cardShadow: themeFromDB.cardShadow || DEFAULT_THEME.cardShadow
      };
      
      setQuizTheme(mappedTheme);
      setBranding(res.branding || {});
    } catch (error) {
      console.error('Failed to load theme:', error);
      setMessage({ type: 'error', text: 'Failed to load theme' });
      // Set defaults on error so the form is still usable
      setQuizTheme(DEFAULT_THEME);
    } finally {
      setLoading(false);
    }
  }, [tenantId, secret]);

  useEffect(() => {
    loadTheme();
  }, [loadTheme]);

  const handleThemeChange = (key, value) => {
    setQuizTheme(prev => ({ ...prev, [key]: value }));
  };

  const handleBrandingChange = (key, value) => {
    setBranding(prev => ({ ...prev, [key]: value }));
  };

  const saveAll = async () => {
    setSaving(true);
    setMessage(null);

    try {
      const mappedTheme = {
        brandColor: quizTheme.primaryColor,
        buttonTextColor: quizTheme.buttonTextColor,
        backgroundColor: quizTheme.backgroundColor,
        textSecondary: quizTheme.textSecondary,
        fontFamily: quizTheme.fontFamily,
        headingFontFamily: quizTheme.headingFontFamily,
        backgroundAccent: quizTheme.backgroundLight,
        textColor: quizTheme.textPrimary,
        buttonStyle: quizTheme.buttonRadius === '999px' ? 'pill' : 
                     quizTheme.buttonRadius === '4px' ? 'square' : 'rounded',
        cardStyle: quizTheme.cardRadius === '4px' ? 'square' : 'rounded',
        cardShadow: quizTheme.cardShadow === 'none' ? 'none' :
                    typeof quizTheme.cardShadow === 'string' && quizTheme.cardShadow.includes('2px') ? 'subtle' :
                    typeof quizTheme.cardShadow === 'string' && quizTheme.cardShadow.includes('12px') ? 'bold' : 'medium'
      };

      console.log('[Branding] Mapped theme for backend:', mappedTheme);

      await adminFetch(`/api/tenant/${tenantId}/theme`, {
        method: 'PUT',
        body: { app: 'quiz', theme: mappedTheme },
        secret
      });

      await adminFetch(`/api/tenant/${tenantId}/branding`, {
        method: 'PUT',
        body: { branding },
        secret
      });

      setMessage({ type: 'success', text: '✅ Theme and branding saved successfully!' });
    } catch (error) {
      console.error('Failed to save:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to save changes' });
    } finally {
      setSaving(false);
    }
  };

  const resetToDefaults = () => {
    if (window.confirm('Reset theme to default values? This cannot be undone.')) {
      setQuizTheme(DEFAULT_THEME);
      setMessage({ type: 'info', text: 'Theme reset to defaults. Click "Save All Changes" to apply.' });
    }
  };

  // Show loading state while fetching theme
  if (loading || !quizTheme) {
    return (
      <div>
        <TopNav />
        <div style={{ padding: '24px', maxWidth: '1600px', margin: '0 auto' }}>
          <div style={{ 
            textAlign: 'center', 
            padding: '60px 20px',
            background: 'white',
            borderRadius: '12px',
            border: '1px solid #e2e8f0'
          }}>
            <div style={{ fontSize: '18px', color: '#64748b', marginBottom: '8px' }}>
              Loading theme...
            </div>
            <div style={{ fontSize: '14px', color: '#94a3b8' }}>
              Please wait while we fetch your branding settings
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <TopNav />
      <div style={{ padding: '24px', maxWidth: '1600px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: '32px' }}>
          <h1 style={{ marginBottom: '8px', fontSize: '32px', fontWeight: 700 }}>
            🎨 Branding & Theme
          </h1>
          <p style={{ color: '#64748b', fontSize: '16px' }}>
            Customize your quiz appearance • Real component previews • ADA compliance checker
          </p>
        </div>

        {/* Message */}
        {message && (
          <div style={{
            padding: '16px 20px',
            borderRadius: '12px',
            marginBottom: '24px',
            backgroundColor: message.type === 'success' ? '#ecfdf5' : message.type === 'error' ? '#fef2f2' : '#eff6ff',
            color: message.type === 'success' ? '#065f46' : message.type === 'error' ? '#991b1b' : '#1e40af',
            border: `2px solid ${message.type === 'success' ? '#10b981' : message.type === 'error' ? '#ef4444' : '#3b82f6'}`,
            fontSize: '15px',
            fontWeight: 600
          }}>
            {message.text}
          </div>
        )}

        {/* Two-column layout */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
          
          {/* LEFT COLUMN - Form Controls */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            
            {/* Branding Section */}
            <Section title="🏫 University Branding">
              <InputField
                label="University Name"
                value={branding.universityName || ''}
                onChange={(val) => handleBrandingChange('universityName', val)}
                placeholder="e.g., Demo University"
              />
              <InputField
                label="Description"
                value={branding.description || ''}
                onChange={(val) => handleBrandingChange('description', val)}
                placeholder="e.g., Find your perfect program"
              />
              <InputField
                label="Logo URL"
                value={branding.logoUrl || ''}
                onChange={(val) => handleBrandingChange('logoUrl', val)}
                placeholder="https://example.com/logo.png"
              />
              <InputField
                label="Favicon URL"
                value={branding.faviconUrl || ''}
                onChange={(val) => handleBrandingChange('faviconUrl', val)}
                placeholder="https://example.com/favicon.ico"
              />
            </Section>

            {/* Primary Colors */}
            <Section title="🎨 Primary Colors">
              <ColorInput label="Primary Color" value={quizTheme.primaryColor} onChange={(val) => handleThemeChange('primaryColor', val)} />
              <ColorInput label="Button Text Color" value={quizTheme.buttonTextColor} onChange={(val) => handleThemeChange('buttonTextColor', val)} />
            </Section>

            {/* Background Colors */}
            <Section title="🎨 Background Colors">
              <ColorInput label="Background" value={quizTheme.backgroundColor} onChange={(val) => handleThemeChange('backgroundColor', val)} />
              <ColorInput label="Background Light" value={quizTheme.backgroundLight} onChange={(val) => handleThemeChange('backgroundLight', val)} />
            </Section>

            {/* Text Colors */}
            <Section title="✍️ Text Colors">
              <ColorInput label="Text Primary" value={quizTheme.textPrimary} onChange={(val) => handleThemeChange('textPrimary', val)} />
              <ColorInput label="Text Secondary" value={quizTheme.textSecondary} onChange={(val) => handleThemeChange('textSecondary', val)} />
            </Section>

            {/* Layout */}
            <Section title="📐 Layout & Spacing">
              <InputField
                label="Button Radius"
                value={quizTheme.buttonRadius}
                onChange={(val) => handleThemeChange('buttonRadius', val)}
                placeholder="e.g., 8px, 12px, 999px"
                hint="Border radius for buttons"
              />
              <InputField
                label="Card Radius"
                value={quizTheme.cardRadius}
                onChange={(val) => handleThemeChange('cardRadius', val)}
                placeholder="e.g., 12px"
                hint="Border radius for cards"
              />
              <InputField
                label="Card Shadow"
                value={quizTheme.cardShadow}
                onChange={(val) => handleThemeChange('cardShadow', val)}
                placeholder="medium, subtle, bold, or none"
                hint="Preset shadow style"
              />
            </Section>

            {/* Typography */}
            <Section title="✍️ Typography">
              <InputField
                label="Body Font"
                value={quizTheme.fontFamily}
                onChange={(val) => handleThemeChange('fontFamily', val)}
                placeholder="-apple-system, sans-serif"
                hint="Font family for body text"
              />
              <InputField
                label="Heading Font"
                value={quizTheme.headingFontFamily}
                onChange={(val) => handleThemeChange('headingFontFamily', val)}
                placeholder="inherit"
                hint="Font family for headings"
              />
            </Section>
          </div>

          {/* RIGHT COLUMN - Live Preview with REAL Quiz Components */}
          <div style={{ position: 'sticky', top: '24px', height: 'fit-content' }}>
            <div style={{
              background: 'white',
              border: '1px solid #e2e8f0',
              borderRadius: '12px',
              padding: '24px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
            }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                marginBottom: '20px' 
              }}>
                <h2 style={{ fontSize: '20px', fontWeight: 700, color: '#0b1b2a', margin: 0 }}>
                  🎭 Live Preview
                </h2>
                <span style={{ 
                  fontSize: '10px', 
                  color: '#0b5fff',
                  background: '#eff6ff',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  fontWeight: 700,
                  letterSpacing: '0.5px'
                }}>
                  REAL COMPONENTS
                </span>
              </div>

              <div style={{
                padding: '20px',
                borderRadius: '12px',
                background: quizTheme.backgroundLight,
                border: '2px dashed #cbd5e1',
                maxHeight: 'calc(100vh - 220px)',
                overflowY: 'auto'
              }}>
                
                {/* 1. ADA COMPLIANCE CHECKER */}
                <ComponentPreview title="🔍 ADA Compliance Check">
                  <ContrastChecker 
                    backgroundColor={quizTheme.primaryColor}
                    textColor={quizTheme.buttonTextColor}
                    label="Primary Button (Normal Text)"
                    size="normal"
                  />
                  <ContrastChecker 
                    backgroundColor={quizTheme.primaryColor}
                    textColor={quizTheme.buttonTextColor}
                    label="Primary Button (Large Text)"
                    size="large"
                  />
                  <ContrastChecker 
                    backgroundColor={quizTheme.backgroundColor}
                    textColor={quizTheme.textPrimary}
                    label="Body Text on White Background"
                    size="normal"
                  />
                  <ContrastChecker 
                    backgroundColor={quizTheme.backgroundColor}
                    textColor={quizTheme.textSecondary}
                    label="Secondary Text on White Background"
                    size="normal"
                  />
                  <ContrastChecker 
                    backgroundColor={quizTheme.backgroundLight}
                    textColor={quizTheme.textPrimary}
                    label="Body Text on Light Background"
                    size="normal"
                  />
                  <ContrastChecker 
                    backgroundColor={quizTheme.backgroundLight}
                    textColor={quizTheme.textSecondary}
                    label="Secondary Text on Light Background"
                    size="normal"
                  />
                </ComponentPreview>

                {/* 2. PHASE HEADER (from QuizDashboard) */}
                <ComponentPreview title="Phase Header Component">
                  <div style={{
                    padding: '20px',
                    background: `linear-gradient(135deg, ${quizTheme.primaryColor}10, ${quizTheme.backgroundLight})`,
                    borderRadius: quizTheme.cardRadius,
                    borderLeft: `4px solid ${quizTheme.primaryColor}`
                  }}>
                    <div style={{ 
                      fontSize: '13px', 
                      color: quizTheme.textSecondary, 
                      marginBottom: '4px',
                      fontWeight: 500,
                      fontFamily: quizTheme.fontFamily
                    }}>
                      Phase 1 of 3
                    </div>
                    <h3 style={{ 
                      margin: '0 0 6px 0', 
                      color: quizTheme.primaryColor,
                      fontSize: '20px',
                      fontWeight: 600,
                      fontFamily: quizTheme.headingFontFamily
                    }}>
                      Let's get to know you!
                    </h3>
                    <p style={{ 
                      margin: 0, 
                      color: quizTheme.textSecondary,
                      fontSize: '14px',
                      fontFamily: quizTheme.fontFamily
                    }}>
                      Help us understand what drives you
                    </p>
                  </div>
                </ComponentPreview>

                {/* 3. QUESTION CARD (from QuestionCard) */}
                <ComponentPreview title="Question Card Component">
                  <div style={{
                    background: quizTheme.backgroundColor,
                    borderRadius: quizTheme.cardRadius,
                    boxShadow: '0 6px 18px rgba(11, 27, 42, 0.06)',
                    padding: '16px'
                  }}>
                    <h4 style={{ 
                      fontSize: '16px',
                      marginBottom: '12px',
                      fontWeight: 600,
                      color: quizTheme.textPrimary,
                      fontFamily: quizTheme.headingFontFamily
                    }}>
                      What interests you most about higher education?
                    </h4>
                    
                    {/* Selected option */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      padding: '10px',
                      borderRadius: quizTheme.buttonRadius,
                      border: `2px solid ${quizTheme.primaryColor}`,
                      background: quizTheme.primaryColor,
                      color: quizTheme.buttonTextColor,
                      marginBottom: '8px',
                      fontFamily: quizTheme.fontFamily
                    }}>
                      <input type="radio" checked readOnly />
                      <div>
                        <div style={{ fontWeight: 500, fontSize: '14px' }}>Career advancement</div>
                        <div style={{ fontSize: '13px', opacity: 0.85 }}>Build professional skills</div>
                      </div>
                    </div>
                    
                    {/* Unselected option */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      padding: '10px',
                      borderRadius: quizTheme.buttonRadius,
                      border: `2px solid ${quizTheme.backgroundLighter}`,
                      background: quizTheme.backgroundColor,
                      fontFamily: quizTheme.fontFamily
                    }}>
                      <input type="radio" readOnly />
                      <div>
                        <div style={{ fontWeight: 500, color: quizTheme.textPrimary, fontSize: '14px' }}>
                          Personal growth
                        </div>
                        <div style={{ fontSize: '13px', color: quizTheme.textSecondary }}>
                          Expand knowledge
                        </div>
                      </div>
                    </div>
                  </div>
                </ComponentPreview>

                {/* 4. NAVIGATION BUTTONS (from QuizWrapper) */}
                <ComponentPreview title="Quiz Navigation Buttons">
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <button style={{
                      padding: '8px 16px',
                      background: 'white',
                      color: quizTheme.textPrimary,
                      border: `2px solid ${quizTheme.backgroundLighter}`,
                      borderRadius: quizTheme.buttonRadius,
                      fontWeight: 600,
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontFamily: quizTheme.fontFamily
                    }}>
                      Previous
                    </button>
                    <button style={{
                      padding: '8px 16px',
                      background: quizTheme.primaryColor,
                      color: quizTheme.buttonTextColor,
                      border: 'none',
                      borderRadius: quizTheme.buttonRadius,
                      fontWeight: 600,
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontFamily: quizTheme.fontFamily,
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }}>
                      Next →
                    </button>
                  </div>
                </ComponentPreview>

                {/* 5. PROGRAM CARD (from ProgramCard) */}
                <ComponentPreview title="Program Card Component">
                  <div style={{
                    background: quizTheme.backgroundColor,
                    borderRadius: quizTheme.cardRadius,
                    boxShadow: '0 6px 18px rgba(11, 27, 42, 0.06)',
                    padding: '14px',
                    display: 'grid',
                    gridTemplateColumns: '70px 1fr',
                    gap: '12px',
                    alignItems: 'start'
                  }}>
                    <div style={{
                      width: '70px',
                      height: '52px',
                      borderRadius: quizTheme.buttonRadius,
                      background: `linear-gradient(135deg, ${quizTheme.primaryColor}20, ${quizTheme.primaryColorLight})`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '24px'
                    }}>
                      🎓
                    </div>
                    
                    <div>
                      <h4 style={{ 
                        margin: '0 0 6px 0',
                        fontSize: '16px',
                        lineHeight: '1.3',
                        color: quizTheme.textPrimary,
                        fontFamily: quizTheme.headingFontFamily
                      }}>
                        Business Administration
                      </h4>
                      <p style={{ 
                        margin: '0 0 10px 0',
                        color: quizTheme.textSecondary,
                        fontSize: '13px',
                        fontFamily: quizTheme.fontFamily
                      }}>
                        Develop leadership skills
                      </p>
                      <button style={{
                        padding: '6px 12px',
                        background: quizTheme.primaryColor,
                        color: quizTheme.buttonTextColor,
                        border: 'none',
                        borderRadius: quizTheme.buttonRadius,
                        fontWeight: 600,
                        cursor: 'pointer',
                        fontSize: '13px',
                        fontFamily: quizTheme.fontFamily
                      }}>
                        View Program →
                      </button>
                    </div>
                  </div>
                </ComponentPreview>

                {/* 6. PROGRESS BAR */}
                <ComponentPreview title="Progress Bar Component">
                  <div>
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between',
                      fontSize: '13px',
                      color: quizTheme.textSecondary,
                      marginBottom: '6px',
                      fontFamily: quizTheme.fontFamily
                    }}>
                      <span>Progress</span>
                      <span>3 of 5</span>
                    </div>
                    <div style={{
                      height: '8px',
                      background: quizTheme.backgroundLighter,
                      borderRadius: quizTheme.buttonRadius,
                      overflow: 'hidden'
                    }}>
                      <div style={{
                        height: '100%',
                        background: quizTheme.primaryColor,
                        width: '60%',
                        transition: 'width 0.3s ease'
                      }} />
                    </div>
                  </div>
                </ComponentPreview>

              </div>

              {/* Preview Note */}
              <p style={{
                marginTop: '16px',
                fontSize: '11px',
                color: '#64748b',
                fontStyle: 'italic',
                textAlign: 'center'
              }}>
                Real quiz components • Updates live • ADA compliant
              </p>
            </div>
          </div>
        </div>

        {/* Action Bar - Fixed at bottom */}
        <div style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          background: 'white',
          borderTop: '2px solid #e2e8f0',
          padding: '16px 24px',
          display: 'flex',
          gap: '12px',
          alignItems: 'center',
          justifyContent: 'space-between',
          boxShadow: '0 -4px 12px rgba(0,0,0,0.05)',
          zIndex: 1000
        }}>
          <button
            onClick={resetToDefaults}
            style={{
              padding: '12px 24px',
              background: 'white',
              color: '#64748b',
              border: '2px solid #e2e8f0',
              borderRadius: '8px',
              fontWeight: 600,
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            ↺ Reset to Defaults
          </button>

          <button
            onClick={saveAll}
            disabled={saving}
            style={{
              padding: '12px 40px',
              background: 'linear-gradient(135deg, #0b5fff 0%, #0847cc 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontWeight: 700,
              cursor: saving ? 'not-allowed' : 'pointer',
              opacity: saving ? 0.6 : 1,
              fontSize: '16px',
              boxShadow: '0 4px 12px rgba(11, 95, 255, 0.3)'
            }}
          >
            {saving ? '💾 Saving...' : '💾 Save All Changes'}
          </button>
        </div>

        {/* Bottom padding */}
        <div style={{ height: '80px' }}></div>
      </div>
    </div>
  );
}

// Section Component
function Section({ title, children }) {
  return (
    <div style={{
      background: 'white',
      border: '1px solid #e2e8f0',
      borderRadius: '12px',
      padding: '20px',
      boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
    }}>
      <h3 style={{ 
        fontSize: '16px', 
        fontWeight: 700, 
        marginBottom: '16px',
        color: '#0b1b2a',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
      }}>
        {title}
      </h3>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {children}
      </div>
    </div>
  );
}

// Color Input Component
function ColorInput({ label, value, onChange }) {
  return (
    <div>
      <label style={{
        display: 'block',
        fontSize: '13px',
        fontWeight: 600,
        marginBottom: '8px',
        color: '#0b1b2a'
      }}>
        {label}
      </label>
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
        <input
          type="color"
          value={value || '#000000'}
          onChange={(e) => onChange(e.target.value)}
          style={{
            width: '50px',
            height: '38px',
            border: '1px solid #e2e8f0',
            borderRadius: '6px',
            cursor: 'pointer'
          }}
        />
        <input
          type="text"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder="#000000"
          style={{
            flex: 1,
            padding: '8px 12px',
            border: '1px solid #e2e8f0',
            borderRadius: '6px',
            fontSize: '13px',
            fontFamily: 'monospace'
          }}
        />
      </div>
    </div>
  );
}

// Input Field Component
function InputField({ label, value, onChange, placeholder, hint }) {
  return (
    <div>
      <label style={{
        display: 'block',
        fontSize: '13px',
        fontWeight: 600,
        marginBottom: '8px',
        color: '#0b1b2a'
      }}>
        {label}
      </label>
      <input
        type="text"
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        style={{
          width: '100%',
          padding: '8px 12px',
          border: '1px solid #e2e8f0',
          borderRadius: '6px',
          fontSize: '14px',
          fontFamily: placeholder?.includes('font') || placeholder?.includes('px') ? 'monospace' : 'inherit'
        }}
      />
      {hint && (
        <p style={{ 
          fontSize: '11px', 
          color: '#94a3b8', 
          marginTop: '4px',
          fontStyle: 'italic'
        }}>
          {hint}
        </p>
      )}
    </div>
  );
}